import { Content, GetContentOptions } from "@robe/core";
import { useState, useEffect } from "react";
import { useXcoreCore } from "./useXcoreCore";

/**
 * `useContentSingletons` hook is expected to always have fetched value, which is true in context of the page where the data is fetched,
 * but if the same component is being used in some other place (for example modal), than the data won't be available.
 * So it is important to use this fallback hook, that will fetch data for the specified content singleton if provided data are not available.
 */
export const useContentSingletonFallback = <T extends Content>(
  contentTypeName: string,
  content: T | null | undefined,
  options?: GetContentOptions<T>
) => {
  const core = useXcoreCore();
  const [data, setData] = useState<T | null | undefined>(content);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleGetSingleton = async () => {
      const res = await core.content.singletonByType<T>(contentTypeName, options);

      if (res) {
        setData(res);
      }

      setIsLoading(false);
    };

    if (!data || data?.type !== contentTypeName) {
      handleGetSingleton();
    } else {
      setIsLoading(false);
    }
  }, [contentTypeName, core.content, data, options]);

  return {
    isLoading,
    data,
  };
};
